import React, { useEffect, useMemo, useRef, useState } from "react";
import { EditorState, convertFromRaw } from "draft-js";
import ViewEditor from "../../components/common/ViewEditor";
import { useHistory } from "react-router-dom";
import {
  HiArrowLeft,
  HiArrowUpTray,
  HiBookmarkSquare,
  HiClock,
  HiDocumentDuplicate,
  HiOutlineCheckCircle,
  HiPencilSquare,
  HiSquares2X2,
  HiXMark,
} from "react-icons/hi2";
import ContentWrapper from "../../components/common/AssessmentContentWrapper";
import MainButton from "../../components/common/MainButtonComponent";
import { routes } from "../../helper/routes";
import ExamFlowConfirmModal from "./ExamFlowConfirmModal";
import { examPaperData } from "./examPaperData";
import "./AssessShared.css";
import "./ExamSection.css";
import { useExamContext } from "../../context/Exam/ExamContext";
import { AzureStorage } from "../../helper/azureStorage";
import config from "../../helper/config";
import { ExamTimer } from "../candidate/ExamTimer";
import ExamQuestionRenderer from "./ExamQuestionRenderer";
import {
  getQuestionId,
  getMainQuestions,
  getSubQuestionMap,
  groupQuestionsByPartAndSection,
  groupQuestionsByPassage,
  sortQuestionsForExam,
} from "./ExamQuestionGrouping";
import { setExamPausedTimerShow } from "../candidate/PauseManage";
import StudentPaperHeader from "./StudentPaperHeader";
import ExamFeedback from "./ExamFeedback";

export default function ExamPaperPage({
  examPaper,
  questions = [],
  examStartData,
  onBack,
  candidateAnswers = [],
}) {
  const history = useHistory();
  //const { saveAnswer, finishExam } = useExamContext();
  const {
    saveAnswersBulk,
    finishExam,
    restartPauseStatus,
    loadRestartPauseStatus,
    restartCurrentExam,
    resetRestartPauseStatus,

  } = useExamContext();

  const [isExitConfirmOpen, setIsExitConfirmOpen] = useState(false);
  const [isSubmitConfirmOpen, setIsSubmitConfirmOpen] = useState(false);
  const [isRestartConfirmOpen, setIsRestartConfirmOpen] = useState(false);
  const [isSinglePageView, setIsSinglePageView] = useState(true);
  const [isExpanded, setIsExpanded] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [activePartKey, setActivePartKey] = useState(null);
  const [activeSectionKey, setActiveSectionKey] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [restartLoading, setRestartLoading] = useState(false);
  const parseBackendDateTime = (value) => {
    if (!value) return null;

    const text = String(value).trim();

    // If backend already sends timezone info, use it as-is.
    if (text.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(text)) {
      return new Date(text);
    }

    // Backend sends UTC time without Z, so force UTC.
    return new Date(`${text}Z`);
  };
  const getExamStartTime = () => {
    const pickedStartTime =
      examStartData?.attempt_start_time ||
      examStartData?.start_time ||
      examStartData?.data?.attempt_start_time ||
      examStartData?.data?.start_time ||
      examPaper?.attempt_start_time ||
      examPaper?.start_time ||
      examPaper?.candidate_start_time ||
      examPaper?.exam_start_time ||
      null;

    return pickedStartTime;
  };

  const getDurationSeconds = () => {
    const interval = Number(examPaper?.exam_time_interval || 0);
    if (!interval) return null;

    const unitId = Number(examPaper?.exam_time_interval_unit_id);

    if (unitId === 2 || examPaper?.exam_time_unit === "hours") {
      return interval * 60 * 60;
    }

    return interval * 60;
  };

  const getInitialRemainingSeconds = () => {
    if (!examPaper?.is_set_time_limit) return null;

    const durationSeconds = getDurationSeconds();
    if (!durationSeconds) return null;

    const fallbackSeconds = durationSeconds;
    const startTime = getExamStartTime();


    if (!startTime) {

      return fallbackSeconds;
    }

    const parsedStartDate = parseBackendDateTime(startTime);
    const startMs = parsedStartDate?.getTime();

    if (!startMs || Number.isNaN(startMs)) {
      console.warn("[TIMER INIT FALLBACK]", {
        reason: "Invalid start time, using full duration",
        startTime,
        durationSeconds,
        fallbackSeconds,
      });

      return fallbackSeconds;
    }

    const endMs = startMs + durationSeconds * 1000;
    const remainingMs = endMs - Date.now();

    return Math.max(Math.floor(remainingMs / 1000), 0);
  };

  const [remainingSeconds, setRemainingSeconds] = useState(
    getInitialRemainingSeconds
  );

  const [isTimerReady, setIsTimerReady] = useState(false);
  const [answers, setAnswers] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [reviewFlags, setReviewFlags] = useState({});
  const [lastSavedAt, setLastSavedAt] = useState("");
  const [saveStatus, setSaveStatus] = useState("saved");
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 992 : false
  );
  const [isMobileSidebarModalOpen, setIsMobileSidebarModalOpen] = useState(false);
  const [pendingScrollQuestionId, setPendingScrollQuestionId] = useState(null);


  const contentScrollRef = useRef(null);
  const questionRefs = useRef({});
  const saveTimeoutRef = useRef(null);
  const latestAnswersRef = useRef({});
  const isMountedRef = useRef(true);
  const isLeavingRef = useRef(false);
  const isSavingRef = useRef(false);
  const leaveLockRef = useRef(false);
  const submitLockRef = useRef(false);
  const savePromiseRef = useRef(null);
  const autoSubmitLockRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;

      if (saveTimeoutRef.current) {
        window.clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const syncViewport = () => {
      setIsMobileViewport(window.innerWidth < 992);
    };

    syncViewport();
    window.addEventListener("resize", syncViewport);

    return () => {
      window.removeEventListener("resize", syncViewport);
    };
  }, []);

  useEffect(() => {
    if (!pendingScrollQuestionId || !isSinglePageView) {
      return;
    }

    let frameId = null;
    let timeoutId = null;

    const runScroll = () => {
      timeoutId = window.setTimeout(() => {
        scrollToQuestion(pendingScrollQuestionId);
        setPendingScrollQuestionId(null);
      }, 0);
    };

    frameId = window.requestAnimationFrame(runScroll);

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }

      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [
    pendingScrollQuestionId,
    isSinglePageView,
    currentQuestionIndex,
    activePartKey,
    activeSectionKey,
  ]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      if (
        saveStatus === "unsaved" &&
        !isLeavingRef.current &&
        !isSavingRef.current
      ) {
        saveCurrentAnswers({ silent: true });
      }
    }, 45000);

    return () => window.clearInterval(intervalId);
  }, [saveStatus]);

  useEffect(() => {
    latestAnswersRef.current = answers;
  }, [answers]);

  useEffect(() => {
    if (!candidateAnswers.length) return;

    const restoredAnswers = {};

    candidateAnswers.forEach((answer) => {
      try {
        restoredAnswers[answer.question_id] = JSON.parse(answer.answer_content);
      } catch {
        restoredAnswers[answer.question_id] = answer.answer_content || "";
      }
    });

    latestAnswersRef.current = restoredAnswers;
    setAnswers(restoredAnswers);
  }, [candidateAnswers]);

  useEffect(() => {
    if (!Object.keys(answers).length) return;
    if (isLeavingRef.current) return;

    setSaveStatus("unsaved");

    if (saveTimeoutRef.current) {
      window.clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = window.setTimeout(() => {
      saveCurrentAnswers({ silent: true });
    }, 10000);

    return () => {
      if (saveTimeoutRef.current) {
        window.clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [answers]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isLeavingRef.current || submitLockRef.current) {
        return;
      }
      event.preventDefault();
      event.returnValue = "";
      return "";

    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const initialSeconds = getInitialRemainingSeconds();



    setRemainingSeconds(initialSeconds);
    setIsTimerReady(true);
  }, [examPaper, examStartData]);

  useEffect(() => {
    const assessmentId = examPaper?.assessment_id || examPaper?.id;

    if (assessmentId) {
      loadRestartPauseStatus(assessmentId);
    }
  }, [examPaper, loadRestartPauseStatus]);


  const questionList = questions;

  const sortedQuestionList = useMemo(
    () => sortQuestionsForExam(questionList),
    [questionList]
  );

  const mainQuestionList = useMemo(
    () => getMainQuestions(sortedQuestionList),
    [sortedQuestionList]
  );

  const subQuestionMap = useMemo(
    () => getSubQuestionMap(sortedQuestionList),
    [sortedQuestionList]
  );

  const groupedQuestionBlocks = useMemo(
    () => groupQuestionsByPartAndSection(mainQuestionList),
    [mainQuestionList]
  );
  const currentMainQuestion = mainQuestionList[currentQuestionIndex];

  useEffect(() => {
    if (!groupedQuestionBlocks.length) return;

    const firstPart = groupedQuestionBlocks[0];
    const firstSection = firstPart.sections?.[0];

    if (!activePartKey && firstPart) {
      setActivePartKey(firstPart.partKey);
    }

    if (!activeSectionKey && firstSection) {
      setActiveSectionKey(firstSection.sectionKey);
    }
  }, [groupedQuestionBlocks, activePartKey, activeSectionKey]);

  useEffect(() => {
    if (mainQuestionList.length === 0) return;

    if (currentQuestionIndex > mainQuestionList.length - 1) {
      setCurrentQuestionIndex(0);
    }
  }, [currentQuestionIndex, mainQuestionList.length]);

  const isDraftAnswerEmpty = (rawDraft) => {
    const blocks = rawDraft?.blocks || [];
    return blocks.every((block) => !String(block?.text || "").trim());
  };

  const isAnswerFilled = (answerValue) => {
    if (Array.isArray(answerValue)) return answerValue.length > 0;

    if (typeof answerValue === "string") {
      return answerValue.trim() !== "";
    }

    if (answerValue && typeof answerValue === "object") {
      const hasFiles =
        Array.isArray(answerValue.files) && answerValue.files.length > 0;

      const hasText =
        answerValue.text && !isDraftAnswerEmpty(answerValue.text);

      return hasFiles || hasText;
    }

    return false;
  };

  const isMainQuestionAnswered = (question) => {
    const questionId = getQuestionId(question);

    if (question.sub_questions) {
      return question.sub_questions
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean)
        .some((subId) => isAnswerFilled(answers[subId]));
    }

    return isAnswerFilled(answers[questionId]);
  };

  const questionStats = useMemo(() => {
    const answered = mainQuestionList.filter(isMainQuestionAnswered).length;

    const reviewed = mainQuestionList.filter((question) => {
      const questionId = getQuestionId(question);
      return Boolean(reviewFlags[questionId]);
    }).length;

    return {
      answered,
      reviewed,
      unanswered: mainQuestionList.length - answered,
    };
  }, [answers, mainQuestionList, reviewFlags]);
  const formattedTime = useMemo(() => {
    if (remainingSeconds === null) return "Unlimited";

    const hours = String(Math.floor(remainingSeconds / 3600)).padStart(2, "0");
    const minutes = String(
      Math.floor((remainingSeconds % 3600) / 60)
    ).padStart(2, "0");
    const seconds = String(remainingSeconds % 60).padStart(2, "0");

    return `${hours}:${minutes}:${seconds}`;
  }, [remainingSeconds]);
  const isLastTwoMinutes =
    remainingSeconds !== null && remainingSeconds <= 120;

  const handleAnswerChange = (questionId, value) => {
    setAnswers((currentAnswers) => ({
      ...currentAnswers,
      [questionId]: value,
    }));

    setSaveStatus("unsaved");
  };

  const handleReviewToggle = (questionId) => {
    setReviewFlags((currentFlags) => ({
      ...currentFlags,
      [questionId]: !currentFlags[questionId],
    }));
  };

  const handleFileDownload = async (questionId, fileName) => {
    try {
      const candidateId = examPaper?.candidate_id;
      if (!candidateId) return;

      const containerName = config.storageBlobConfigs.containerName;
      const cleanFileName = String(fileName).replace(/['"]+/g, "");
      const blobName = `${candidateId}-${questionId}-${cleanFileName}`;

      await AzureStorage.downloadBlob(containerName, blobName);
    } catch (error) {
      console.error("File download failed", error);
      window.Swal.fire("Failed!", "File download failed. Please try again.", "error");
    }
  };

  const saveCurrentAnswers = async ({ silent = false } = {}) => {
    const currentAnswers = latestAnswersRef.current;

    if (!Object.keys(currentAnswers).length) return null;

    if (savePromiseRef.current) {
      return savePromiseRef.current;
    }

    savePromiseRef.current = (async () => {
      try {
        isSavingRef.current = true;

        if (!silent && isMountedRef.current) {
          setSaveStatus("saving");
        }

        const response = await saveAnswersBulk(currentAnswers);

        if (isMountedRef.current) {
          const now = new Date();

          setLastSavedAt(
            now.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            })
          );

          setSaveStatus("saved");
        }

        return response;
      } catch (error) {
        console.error("Failed to save answers", error);

        if (isMountedRef.current) {
          setSaveStatus("error");
        }

        throw error;
      } finally {
        isSavingRef.current = false;
        savePromiseRef.current = null;
      }
    })();

    return savePromiseRef.current;
  };

  const handleSave = async () => {
    await saveCurrentAnswers();
  };

  const isAllMcqPaper = useMemo(() => {
    const gradableQuestions = questions.filter((q) => {
      const marks = Number(q.mark_allocation || 0);

      if (q.is_subquestion === "true") return marks > 0;

      if (q.question_type !== "Sub Question") return marks > 0;

      return false;
    });

    return (
      gradableQuestions.length > 0 &&
      gradableQuestions.every((q) => q.question_type === "MCQ")
    );
  }, [questions]);

  const submitExamNow = async ({ isAutoSubmit = false } = {}) => {
    if (submitLockRef.current) return;

    submitLockRef.current = true;
    setSubmitLoading(true);

    try {
      isLeavingRef.current = true;

      if (saveTimeoutRef.current) {
        window.clearTimeout(saveTimeoutRef.current);
      }

      await saveCurrentAnswers({ silent: isAutoSubmit });

      await finishExam({
        answers: latestAnswersRef.current,
        is_all_mcq: isAllMcqPaper,
        stayOnPaper: true,
      });

      if (isMountedRef.current) {
        setIsSubmitConfirmOpen(false);
      }

      if (isAutoSubmit) {
        await window.Swal.fire({
          title: "Time is up",
          text: "Your exam has been submitted automatically.",
          icon: "warning",
          allowOutsideClick: false,
          allowEscapeKey: false,
          confirmButtonText: "OK",
        });

        if (isMountedRef.current) {
          setShowRatingModal(true);
          setSubmitLoading(false);
        }
        return;
      }

      await window.Swal.fire({
        title: "Submission Complete",
        text: "Exam Finished Successfully",
        icon: "success",
        showConfirmButton: true,
        allowOutsideClick: false,
        confirmButtonText: "OK",
      });

      if (isMountedRef.current) {
        setShowRatingModal(true);
        setSubmitLoading(false);
      }
    } catch (error) {
      console.error("Failed to finish exam", error);

      submitLockRef.current = false;
      autoSubmitLockRef.current = false;

      if (isMountedRef.current) {
        setSubmitLoading(false);
      }

      if (isAutoSubmit) {
        window.Swal.fire(
          "Auto-submit failed",
          "Please check your connection and try submitting again.",
          "error"
        );
      }
    }
  };

  const handleSubmitAndFinish = async () => {
    await submitExamNow({ isAutoSubmit: false });
  };

  const scrollToQuestion = (questionId) => {
    const contentElement = contentScrollRef.current;
    const questionElement = questionRefs.current[questionId];

    if (!contentElement || !questionElement) {
      return;
    }

    contentElement.scrollTo({
      top: Math.max(questionElement.offsetTop - 10, 0),
      behavior: "smooth",
    });
  };

  const getPlainTextFromDraft = (raw) => {
    try {
      const blocks = raw?.blocks || [];
      return blocks.map((block) => block.text).join("\n");
    } catch {
      return "";
    }
  };

  const parseQuestionContent = (content) => {
    try {
      const parsed = JSON.parse(content || "[]");
      return {
        prompt: getPlainTextFromDraft(parsed[0]),
        options: parsed.slice(1).map(getPlainTextFromDraft).filter(Boolean),
      };
    } catch {
      return {
        prompt: content || "",
        options: [],
      };
    }
  };

  const activePart = groupedQuestionBlocks.find(
    (part) => String(part.partKey) === String(activePartKey)
  );

  const activeSection = activePart?.sections?.find(
    (section) => String(section.sectionKey) === String(activeSectionKey)
  );

  const activePartQuestions =
    activeSection?.questions?.map((question) => ({
      question,
      sectionKey: activeSection.sectionKey,
    })) || [];


  const goToQuestion = (question) => {
    const questionId = getQuestionId(question);

    const realIndex = mainQuestionList.findIndex(
      (item) => String(getQuestionId(item)) === String(questionId)
    );

    if (realIndex < 0) return;

    setCurrentQuestionIndex(realIndex);

    const part = groupedQuestionBlocks.find((partGroup) =>
      partGroup.sections.some((sectionGroup) =>
        sectionGroup.questions.some(
          (item) => String(getQuestionId(item)) === String(questionId)
        )
      )
    );

    const section = part?.sections.find((sectionGroup) =>
      sectionGroup.questions.some(
        (item) => String(getQuestionId(item)) === String(questionId)
      )
    );

    if (part) setActivePartKey(part.partKey);
    if (section) setActiveSectionKey(section.sectionKey);

    if (isSinglePageView) {
      setPendingScrollQuestionId(questionId);
    }
  };

  const getQuestionDisplayNumber = (question) => {
    const questionId = getQuestionId(question);

    for (const partGroup of groupedQuestionBlocks) {
      for (const sectionGroup of partGroup.sections) {
        const sectionIndex = sectionGroup.questions.findIndex(
          (item) => String(getQuestionId(item)) === String(questionId)
        );



        if (sectionIndex !== -1) {
          return sectionIndex + 1;
        }
      }
    }

    return 1;
  };

  const renderQuestionGroup = (question, showPassage = true) => {
    const questionId = getQuestionId(question);

    return (
      <div
        key={questionId}
        ref={(element) => {
          questionRefs.current[questionId] = element;
        }}
      >
        <ExamQuestionRenderer
          question={question}
          displayNumber={getQuestionDisplayNumber(question)}
          showPassage={showPassage}
          answers={answers}
          reviewFlags={reviewFlags}
          onAnswerChange={handleAnswerChange}
          onEditorAnswerChange={handleEditorAnswerChange}
          onReviewToggle={handleReviewToggle}
          subQuestionMap={subQuestionMap}
          onFileUpload={handleFileUpload}
          onFileDelete={handleFileDelete}
          uploadingFiles={uploadingFiles}
          onFileDownload={handleFileDownload}
        />
      </div>
    );
  };

  const getAnswerObject = (questionId) => {
    const value = latestAnswersRef.current[questionId];

    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      ("text" in value || "files" in value)
    ) {
      return {
        text: value.text || null,
        files: Array.isArray(value.files) ? value.files : [],
      };
    }

    if (Array.isArray(value)) {
      return { text: null, files: value };
    }

    return { text: value || null, files: [] };
  };

  const handleEditorAnswerChange = (questionId, rawAnswer) => {
    const current = getAnswerObject(questionId);

    handleAnswerChange(questionId, {
      ...current,
      text: rawAnswer,
    });
  };

  const handleFileUpload = async (event, questionId) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    event.target.value = "";

    const candidateId = examPaper?.candidate_id;
    if (!candidateId) return;

    const fileName = file.name;
    const fileSize = file.size;
    const maxSizeBytes = Number(config.fileUploadConfigs.maxFileSize) * 1024 * 1024;

    const current = getAnswerObject(questionId);
    const files = Array.isArray(current.files) ? [...current.files] : [];

    if (!fileName) {
      window.Swal.fire("Failed!", "File name is empty.", "error");
      return;
    }

    if (files.includes(fileName)) {
      window.Swal.fire(
        "Failed!",
        "File names are similar. Delete the previous upload to add a new file with same name!",
        "error"
      );
      return;
    }

    if (files.length >= Number(config.fileUploadConfigs.maxFileCount)) {
      window.Swal.fire(
        "Failed!",
        `Maximum ${config.fileUploadConfigs.maxFileCount} file(s) can be uploaded.`,
        "error"
      );
      return;
    }

    if (fileSize > maxSizeBytes) {
      window.Swal.fire(
        "Failed!",
        `File cannot be uploaded. Max File Size is ${config.fileUploadConfigs.maxFileSize} MB.`,
        "error"
      );
      return;
    }

    try {
      setUploadingFiles((prev) => ({ ...prev, [questionId]: true }));

      const containerName = config.storageBlobConfigs.containerName;
      const blobName = `${candidateId}-${questionId}-${fileName}`;

      const reader = new FileReader();

      reader.onload = async (readerEvent) => {
        try {
          await AzureStorage.uploadBlob(
            containerName,
            blobName,
            readerEvent.target.result
          );

          const latestCurrent = getAnswerObject(questionId);
          const latestFiles = Array.isArray(latestCurrent.files)
            ? latestCurrent.files
            : [];

          const nextAnswer = {
            ...latestCurrent,
            files: [...latestFiles, fileName],
          };

          latestAnswersRef.current = {
            ...latestAnswersRef.current,
            [questionId]: nextAnswer,
          };

          handleAnswerChange(questionId, nextAnswer);
          await saveCurrentAnswers({ silent: true });
        } catch (error) {
          console.error("File upload failed", error);
          window.Swal.fire("Failed!", "File upload failed. Please try again.", "error");
        } finally {
          if (isMountedRef.current) {
            setUploadingFiles((prev) => ({ ...prev, [questionId]: false }));
          }
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error("File upload failed", error);
      setUploadingFiles((prev) => ({ ...prev, [questionId]: false }));
      window.Swal.fire("Failed!", "File upload failed. Please try again.", "error");
    }
  };

  const handleFileDelete = async (questionId, fileName) => {
    const candidateId = examPaper?.candidate_id;
    if (!candidateId) return;

    const current = getAnswerObject(questionId);
    const files = Array.isArray(current.files) ? [...current.files] : [];

    try {
      const containerName = config.storageBlobConfigs.containerName;
      const cleanFileName = String(fileName).replace(/['"]+/g, "");
      const blobName = `${candidateId}-${questionId}-${cleanFileName}`;

      await AzureStorage.deleteblob(containerName, blobName);

      const nextAnswer = {
        ...current,
        files: files.filter((item) => item !== cleanFileName),
      };

      latestAnswersRef.current = {
        ...latestAnswersRef.current,
        [questionId]: nextAnswer,
      };

      handleAnswerChange(questionId, nextAnswer);
      await saveCurrentAnswers({ silent: true });
    } catch (error) {
      console.error("File delete failed", error);
      window.Swal.fire("Failed!", "File delete failed. Please try again.", "error");
    }
  };

  const getTimerMilliseconds = () => {
    if (!examPaper?.is_set_time_limit) return null;

    const durationSeconds = getDurationSeconds();
    if (!durationSeconds) return null;

    return remainingSeconds !== null
      ? remainingSeconds * 1000
      : durationSeconds * 1000;
  };

  const timerStorageKey = [
    examPaper?.candidate_id,
    examPaper?.assessment_id || examPaper?.id,
    examPaper?.attempt_no || 1,
  ].join(":");

  const renderTimerContent = ({ mobile = false } = {}) => {
    if (examPaper?.is_set_time_limit) {
      return (
        <ExamTimer
          key={timerStorageKey}
          storageKey={timerStorageKey}
          time_int={getTimerMilliseconds()}
          is_pause={restartPauseStatus?.is_pause}
          pause_until={restartPauseStatus?.pause_until}
          onComplete={() => {
            if (!autoSubmitLockRef.current && !submitLockRef.current) {
              autoSubmitLockRef.current = true;
              submitExamNow({ isAutoSubmit: true });
            }
          }}
          renderExtra={
            !mobile && restartPauseStatus?.is_restart
              ? () => (
                <button
                  className="sidebar-restart-btn"
                  type="button"
                  onClick={() => setIsRestartConfirmOpen(true)}
                >
                  Restart
                </button>
              )
              : undefined
          }
        />
      );
    }

    return (
      <>
        <span>
          <HiClock aria-hidden="true" />
          Time Remaining:
        </span>
        <strong className="exam-paper-page__timer-value">Unlimited</strong>
      </>
    );
  };

  const renderExamSidebarContent = ({ includeTimer = true, includeCollapse = false } = {}) => (
    <div className="exam-paper-sidebar__inner">
      {includeCollapse && (
        <div className="exam-paper-desktop-sidebar__header">
          <button
            type="button"
            className="exam-paper-collapse-btn"
            onClick={() => setIsExpanded(false)}
          >
            &gt; Collapse
          </button>
        </div>
      )}

      {includeTimer ? (
        <div className="exam-paper-page__timer exam-paper-page__timer--sidebar">
          {renderTimerContent()}
        </div>
      ) : null}

      {groupedQuestionBlocks.length > 0 && (
        <div className="exam-paper-sidebar__section exam-paper-nav-panel">
          <p className="exam-paper-nav-title">Navigate to Parts</p>

          <div className="exam-paper-nav-tabs">
            {groupedQuestionBlocks.map((partGroup) => {
              const isActive = String(activePartKey) === String(partGroup.partKey);
              const firstSection = partGroup.sections?.[0];

              return (
                <button
                  type="button"
                  key={partGroup.partKey}
                  className={`exam-paper-nav-tab ${isActive ? "active" : ""}`}
                  onClick={() => {
                    setActivePartKey(partGroup.partKey);

                    if (firstSection) {
                      setActiveSectionKey(firstSection.sectionKey);
                    }

                    const firstQuestion = firstSection?.questions?.[0];
                    if (firstQuestion) {
                      goToQuestion(firstQuestion);
                    }
                  }}
                >
                  {partGroup.partName || "Part"}
                </button>
              );
            })}
          </div>

          {activePart && (
            <>
              <p className="exam-paper-nav-title">Navigate to Sections</p>

              <div className="exam-paper-nav-tabs">
                {activePart.sections.map((sectionGroup) => {
                  const isActive =
                    String(activeSectionKey) === String(sectionGroup.sectionKey);

                  return (
                    <button
                      type="button"
                      key={sectionGroup.sectionKey}
                      className={`exam-paper-nav-tab ${isActive ? "active" : ""}`}
                      onClick={() => {
                        setActiveSectionKey(sectionGroup.sectionKey);

                        const firstQuestion = sectionGroup.questions?.[0];
                        if (firstQuestion) {
                          goToQuestion(firstQuestion);
                        }
                      }}
                    >
                      {sectionGroup.sectionName || "Section"}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {activePartQuestions.length > 0 && (
            <>
              <p className="exam-paper-nav-title">Navigate to Questions in Part</p>

              <div className="exam-paper-sidebar__numbers">
                {activePartQuestions.map(({ question, sectionKey }, localIndex) => {
                  const questionId = getQuestionId(question);
                  const realIndex = mainQuestionList.findIndex(
                    (item) => String(getQuestionId(item)) === String(questionId)
                  );

                  const isAnswered = isMainQuestionAnswered(question);
                  const isReviewed = Boolean(reviewFlags[questionId]);
                  const isActive = realIndex === currentQuestionIndex;
                  const isInActiveSection =
                    String(sectionKey) === String(activeSectionKey);

                  return (
                    <button
                      type="button"
                      key={questionId}
                      className={`exam-question-pill ${isActive ? "exam-question-pill--active" : ""
                        } ${isAnswered ? "exam-question-pill--answered" : ""} ${isReviewed ? "exam-question-pill--reviewed" : ""
                        } ${isInActiveSection ? "exam-question-pill--in-section" : ""}`}
                      onClick={() => {
                        goToQuestion(question);
                        if (isMobileViewport) {
                          setIsMobileSidebarModalOpen(false);
                        }
                      }}
                    >
                      {localIndex + 1}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      <div className="exam-paper-sidebar__section">
        <label className="exam-paper-sidebar__single-view">
          <input
            type="checkbox"
            checked={isSinglePageView}
            onChange={() =>
              setIsSinglePageView((currentValue) => !currentValue)
            }
          />
          <span>Single page view</span>
        </label>
      </div>

      <div className="exam-paper-sidebar__section">
        <MainButton
          action="exit"
          label={leaveLoading ? "Leaving..." : "Exit from Exam"}
          tone="neutral"
          disabled={leaveLoading || submitLoading}
          className="exam-paper-sidebar__save-btn"
          onClick={() => {
            if (!leaveLoading && !submitLoading) {
              setIsMobileSidebarModalOpen(false);
              setIsExitConfirmOpen(true);
            }
          }}
        />
        <MainButton
          action="save"
          icon="save"
          label="Save"
          tone="neutral"
          disabled={saveStatus === "saving" || leaveLoading || submitLoading}
          className="exam-paper-sidebar__save-btn"
          onClick={handleSave}
        />
        <MainButton
          action="submit"
          icon="submit"
          label={submitLoading ? "Submitting..." : "Submit and Finish"}
          disabled={submitLoading || leaveLoading}
          tone="warning"
          className="exam-paper-sidebar__submit-btn"
          onClick={() => {
            if (!submitLoading && !leaveLoading) {
              setIsMobileSidebarModalOpen(false);
              setIsSubmitConfirmOpen(true);
            }
          }}
        />
      </div>

      <div className="exam-paper-sidebar__legend row g-2">
        <div className="col-4 px-0">
          <div className="exam-stat-card">
            <span>Answered</span>
            <strong>{questionStats.answered}</strong>
          </div>
        </div>
        <div className="col-4 px-0">
          <div className="exam-stat-card">
            <span>Unanswered</span>
            <strong>{questionStats.unanswered}</strong>
          </div>
        </div>
        <div className="col-4 px-0">
          <div className="exam-stat-card">
            <span>Reviewed</span>
            <strong>{questionStats.reviewed}</strong>
          </div>
        </div>
      </div>
    </div>
  );


  const examFeedbackSummary = useMemo(() => {
    return {
      assessment_title:
        examPaper?.assessment_details?.title ||
        examPaper?.name ||
        examPaper?.title ||
        "",
      subject:
        examPaper?.assessment_details?.subject ||
        examPaper?.subject ||
        "",
      time_duration:
        examPaper?.assessment_details?.time_duration ||
        examPaper?.exam_time_interval ||
        null,
      questionCount: mainQuestionList.length,
      answeredCount: questionStats.answered,
      examStartTime:
        examStartData?.attempt_start_time ||
        examStartData?.start_time ||
        examStartData?.data?.attempt_start_time ||
        examStartData?.data?.start_time ||
        examPaper?.attempt_start_time ||
        examPaper?.start_time ||
        null,
      examFinishTime: new Date().toISOString(),
    };
  }, [examPaper, examStartData, mainQuestionList.length, questionStats.answered]);

  const handleFeedbackClose = ({ action } = {}) => {
    setShowRatingModal(false);

    if (action !== "success" || !examPaper?.candidate_id) {
      onBack();
      return;
    }

    const gradeReportExam = {
      ...examPaper,
      assessment_id: examPaper.assessment_id || examPaper.id,
      attempt_no: examPaper.attempt_no || 1,
      attempt_start_time:
        examStartData?.attempt_start_time ||
        examStartData?.start_time ||
        examStartData?.data?.attempt_start_time ||
        examStartData?.data?.start_time ||
        examPaper.attempt_start_time ||
        examPaper.start_time ||
        null,
    };

    history.replace({
      pathname: routes.exm_grade_report.replace(
        ":id",
        gradeReportExam.candidate_id
      ),
      search: `?assessment_id=${gradeReportExam.assessment_id}&attempt_no=${gradeReportExam.attempt_no}`,
      state: { exam: gradeReportExam },
    });
  };

  if (showRatingModal) {
    return (
      <ExamFeedback
        onClose={handleFeedbackClose}
        assessmentID={examPaper?.assessment_id || examPaper?.id}
        candidate_id={examPaper?.candidate_id}
        examSummary={examFeedbackSummary}
      />
    );
  }

  return (
    <ContentWrapper>
      <section className="exam-paper-page">
        {isMobileViewport ? (
          <div className="exam-paper-mobile-bar">
            <div className="exam-paper-mobile-bar__timer">
              {renderTimerContent({ mobile: true })}
            </div>
            <button
              type="button"
              className="exam-paper-mobile-bar__menu"
              onClick={() => setIsMobileSidebarModalOpen(true)}
              aria-label="Open exam navigation"
              title="Open exam navigation"
            >
              <HiSquares2X2 aria-hidden="true" />
            </button>
          </div>
        ) : null}

        <div className="exam-paper-layout row g-0">
          <div className={!isMobileViewport && isExpanded ? "col-lg-9" : "col-12"}>
            <div className={`exam-paper-page__main ${!isExpanded ? 'exam-paper-page__main--collapsed' : ''}`}>
              <div className="exam-paper-page__content" ref={contentScrollRef}>
                <div className="exam-paper-page__title-row">
                  {/* <button
                    type="button"
                    className="exam-paper-page__back-btn"
                    // onClick={() => setIsExitConfirmOpen(true)}
                    disabled={leaveLoading || submitLoading}
                    onClick={() => {
                      if (!leaveLoading && !submitLoading) {
                        setIsExitConfirmOpen(true);
                      }
                    }}
                    aria-label="Exit from exam"
                    title="Exit from exam"
                  >
                    <HiArrowLeft aria-hidden="true" />
                    <span>Exit from exam</span>
                  </button> */}


                  <div className="exam-paper-page__title">
                    <StudentPaperHeader assessment={examPaper} />
                    <div className="exam-paper-save-row">
                      <p className="exam-paper-save-status">
                        {saveStatus === "saving" && "Saving..."}
                        {saveStatus === "unsaved" && "Unsaved changes"}
                        {saveStatus === "error" && "Save failed. Please try again."}
                        {saveStatus === "saved" &&
                          (lastSavedAt
                            ? `Last saved at ${lastSavedAt}`
                            : "Answer the questions and save your progress.")}
                      </p>
                      {!isMobileViewport && !isExpanded && (
                        <button
                          type="button"
                          className="exam-paper-expand-btn"
                          onClick={() => setIsExpanded(true)}
                        >
                          &lt; Expand
                        </button>
                      )}
                    </div>

                    {!isMobileViewport && !isExpanded && (
                      <div className="exam-paper-collapsed-toolbar">
                        <div className="exam-paper-collapsed-toolbar__left">
                          <MainButton
                            action="exit"
                            label={leaveLoading ? "Leaving..." : "Exit from Exam"}
                            tone="neutral"
                            disabled={leaveLoading || submitLoading}
                            onClick={() => {
                              if (!leaveLoading && !submitLoading) {
                                setIsExitConfirmOpen(true);
                              }
                            }}
                          />
                          <MainButton
                            action="save"
                            icon="save"
                            label="Save"
                            tone="neutral"
                            disabled={saveStatus === "saving" || leaveLoading || submitLoading}
                            onClick={handleSave}
                          />
                        </div>
                        <div className="exam-paper-collapsed-toolbar__center">
                          <div className="exam-paper-page__timer exam-paper-page__timer--collapsed">
                            {renderTimerContent({ mobile: false })}
                          </div>
                        </div>
                        <div className="exam-paper-collapsed-toolbar__right">
                          <MainButton
                            action="submit"
                            icon="submit"
                            label={submitLoading ? "Submitting..." : "Submit and Finish"}
                            disabled={submitLoading || leaveLoading}
                            tone="warning"
                            onClick={() => {
                              if (!submitLoading && !leaveLoading) {
                                setIsSubmitConfirmOpen(true);
                              }
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>


                </div>

                {isSinglePageView ? (
                  <div className="exam-paper-page__questions">
                    {groupedQuestionBlocks.map((partGroup) => (
                      <div className="exam-part-block" key={partGroup.partKey}>
                        {partGroup.partName && (
                          <div className="exam-part-header">
                            <h2>{partGroup.partName}</h2>
                            {partGroup.partInstruction && <p>{partGroup.partInstruction}</p>}
                          </div>
                        )}

                        {partGroup.sections.map((sectionGroup) => (
                          <div className="exam-section-block" key={sectionGroup.sectionKey}>
                            {sectionGroup.sectionName && (
                              <div className="exam-section-header">
                                <h3>{sectionGroup.sectionName}</h3>
                                {sectionGroup.sectionInstruction && (
                                  <p>{sectionGroup.sectionInstruction}</p>
                                )}
                              </div>
                            )}

                            {/* {sectionGroup.questions.map((question) => renderQuestionGroup(question))} */}
                            {groupQuestionsByPassage(sectionGroup.questions).map(
                              (passageGroup, groupIndex) => (
                                <React.Fragment
                                  key={
                                    passageGroup.passage_id
                                      ? `passage-${passageGroup.passage_id}`
                                      : `question-${groupIndex}`
                                  }
                                >
                                  {passageGroup.passage_id && passageGroup.passage_content && (
                                    <div className="exam-question-card">
                                      <div className="exam-question-passage">
                                        {(() => {
                                          try {
                                            const parsed = JSON.parse(
                                              passageGroup.passage_content || "[]"
                                            );

                                            const passageRaw = Array.isArray(parsed)
                                              ? parsed[0]
                                              : parsed;

                                            return passageRaw ? (
                                              <ViewEditor
                                                editorState={EditorState.createWithContent(
                                                  convertFromRaw(passageRaw)
                                                )}
                                              />
                                            ) : null;
                                          } catch {
                                            return null;
                                          }
                                        })()}
                                      </div>
                                    </div>
                                  )}

                                  {passageGroup.questions.map((question) =>
                                    renderQuestionGroup(
                                      question,
                                      !passageGroup.passage_id
                                    )
                                  )}
                                </React.Fragment>
                              )
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="exam-paper-page__questions">
                    {currentMainQuestion && (
                      <ExamQuestionRenderer
                        question={currentMainQuestion}
                        displayNumber={getQuestionDisplayNumber(currentMainQuestion)}
                        answers={answers}
                        reviewFlags={reviewFlags}
                        onAnswerChange={handleAnswerChange}
                        onEditorAnswerChange={handleEditorAnswerChange}
                        onReviewToggle={handleReviewToggle}
                        subQuestionMap={subQuestionMap}
                        onFileUpload={handleFileUpload}
                        onFileDelete={handleFileDelete}
                        uploadingFiles={uploadingFiles}
                        onFileDownload={handleFileDownload}
                      />
                    )}

                    <div className="exam-paper-page__pager">
                      <MainButton
                        action="prev"
                        icon="prev"
                        label="Previous"
                        tone="neutral"
                        className="exam-paper-page__prev-btn"
                        disabled={currentQuestionIndex === 0}
                        onClick={() =>
                          setCurrentQuestionIndex((value) => Math.max(value - 1, 0))
                        }
                      />

                      <MainButton
                        action="next"
                        icon="next"
                        label={
                          currentQuestionIndex === mainQuestionList.length - 1
                            ? "Done"
                            : "Next"
                        }
                        className="exam-paper-page__next-btn"
                        disabled={mainQuestionList.length === 0}
                        onClick={() =>
                          setCurrentQuestionIndex((value) =>
                            Math.min(value + 1, mainQuestionList.length - 1)
                          )
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {!isMobileViewport && isExpanded && (
            <div className="col-lg-3 px-0">
              <div className="exam-paper-desktop-sidebar">
                {renderExamSidebarContent({ includeTimer: true, includeCollapse: true })}
              </div>
            </div>
          )}
        </div>

        {isMobileSidebarModalOpen ? (
          <div
            className={
              isMobileViewport ? "exam-paper-mobile-modal" : "exam-paper-desktop-drawer"
            }
            role="dialog"
            aria-modal={isMobileViewport ? "true" : "false"}
          >
            <button
              type="button"
              className={
                isMobileViewport
                  ? "exam-paper-mobile-modal__backdrop"
                  : "exam-paper-desktop-drawer__backdrop"
              }
              onClick={() => setIsMobileSidebarModalOpen(false)}
              aria-label="Close exam navigation"
            />
            <div
              className={
                isMobileViewport
                  ? "exam-paper-mobile-modal__sheet"
                  : "exam-paper-mobile-modal__sheet exam-paper-desktop-drawer__sheet"
              }
            >
              <div className="exam-paper-mobile-modal__header">
                <h2>Exam Navigation</h2>
                <button
                  type="button"
                  className="exam-paper-mobile-modal__close"
                  onClick={() => setIsMobileSidebarModalOpen(false)}
                  aria-label="Close exam navigation"
                >
                  <HiXMark aria-hidden="true" />
                </button>
              </div>
              <div className="exam-paper-mobile-modal__body">
                {renderExamSidebarContent({ includeTimer: false })}
              </div>
            </div>
          </div>
        ) : null}

        {(leaveLoading || submitLoading) && (
          <div className="exam-action-lock" />
        )}
        <ExamFlowConfirmModal
          open={isExitConfirmOpen}
          title="Leave exam mode?"
          message="Going back will leave the current exam area and return you to the exams list."
          // confirmLabel="Leave"
          confirmLabel={leaveLoading ? "Leaving..." : "Leave"}
          loading={leaveLoading}
          disabled={leaveLoading}
          onConfirm={async () => {
            if (leaveLockRef.current) return;

            leaveLockRef.current = true;
            setLeaveLoading(true);

            try {
              isLeavingRef.current = true;

              if (saveTimeoutRef.current) {
                window.clearTimeout(saveTimeoutRef.current);
              }

              await saveCurrentAnswers();

              if (isMountedRef.current) {
                setIsExitConfirmOpen(false);
              }

              onBack();
            } catch (error) {
              console.error("Failed while leaving exam", error);
              leaveLockRef.current = false;

              if (isMountedRef.current) {
                setLeaveLoading(false);
              }
            }
          }}
          onClose={() => {
            if (!leaveLoading) {
              setIsExitConfirmOpen(false);
            }
          }}
        //onConfirm={() => history.push(routes.exm)}
        //onClose={() => setIsExitConfirmOpen(false)}
        />

        <ExamFlowConfirmModal
          open={isSubmitConfirmOpen}
          title="Submit and finish?"
          message="This will end the exam mode and return you to the exams list."
          // confirmLabel="Submit"
          confirmLabel={submitLoading ? "Submitting..." : "Submit"}
          loading={submitLoading}
          disabled={submitLoading}
          //onConfirm={() => history.push(routes.exm)}
          onConfirm={handleSubmitAndFinish}
          //onClose={() => setIsSubmitConfirmOpen(false)}
          onClose={() => {
            if (!submitLoading) {
              setIsSubmitConfirmOpen(false);
            }
          }}
        />

        <ExamFlowConfirmModal
          open={isRestartConfirmOpen}
          title="Restart exam?"
          message="Your answers and marked questions will be reset."
          confirmLabel={restartLoading ? "Restarting..." : "Restart"}
          loading={restartLoading}
          disabled={restartLoading}
          onConfirm={async () => {
            if (restartLoading) return;

            setRestartLoading(true);

            try {
              localStorage.removeItem("examMainTimeRemaining");
              localStorage.removeItem("examPauseTimeRemaining");
              localStorage.removeItem("examPauseState");
              localStorage.removeItem("examPauseEndTime");

              setExamPausedTimerShow(false);
              resetRestartPauseStatus();

              const restartResult = await restartCurrentExam(examPaper);
              const assessmentId = examPaper?.assessment_id || examPaper?.id;
              if (assessmentId) {
                await loadRestartPauseStatus(assessmentId);
              }

              latestAnswersRef.current = {};
              setAnswers({});
              setReviewFlags({});
              setCurrentQuestionIndex(0);
              setSaveStatus("saved");
              setLastSavedAt("");

              const initialSeconds = getDurationSeconds();
              setRemainingSeconds(initialSeconds);
              if (groupedQuestionBlocks.length > 0) {
                const firstPart = groupedQuestionBlocks[0];
                const firstSection = firstPart.sections?.[0];

                setActivePartKey(firstPart.partKey);
                setActiveSectionKey(firstSection?.sectionKey || null);
              }
              setIsRestartConfirmOpen(false);
            } finally {
              setRestartLoading(false);
            }
          }}
          onClose={() => {
            if (!restartLoading) {
              setIsRestartConfirmOpen(false);
            }
          }}
        />
      </section>
    </ContentWrapper>
  );
}
